import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { Button } from '@osmosis/tailwind-ui/components/Button';

import React, { useMemo, useState, ChangeEvent } from 'react';
import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { FormattedMessage } from 'react-intl';

import { PlaylistGroupProps } from '@osmosis/shared/types/playlist';
import PlaylistGroupAccordion from './PlaylistGroupAccordion';
import { ModalHookProps } from '../../../hooks/useModal';
import {
  filterPlaylistsByTitle,
  formatePlaylistGroups,
  sortPlaylists,
} from '../../../lib/shared/playlist/utils';
import Divider from '../../../components/Divider';
import PlaylistSortByButton from './PlaylistSortByButton';
import PlaylistSearchField from './PlaylistSearchField';
import { textBlue, bg100 } from '@osmosis/tailwind-ui/styles';

type Props = {
  playlistGroups: PlaylistGroupProps[];
  newPlaylistModal: ModalHookProps;
};

/**
 * Maps playlist groups and shows a button to create a new playlist.
 */
const PlaylistsSideNav = (props: Props): JSX.Element => {
  const { playlistGroups, newPlaylistModal } = props;
  const [searchValue, setSearchValue] = useState('');
  const [sortValue, setSortValue] = useState('');

  const formattePlaylistGroups = useMemo(
    () =>
      formatePlaylistGroups(playlistGroups).filter(
        (item) => !(item.type === 1 && item.playlists.length === 0),
      ),
    [playlistGroups],
  );

  const countPlaylists = (groups: PlaylistGroupProps[]): number => {
    const count = groups.reduce((acc, group) => {
      return acc + group.playlists.length;
    }, 0);
    return count;
  };

  const filteredGroups = useMemo(
    () => filterPlaylistsByTitle(formattePlaylistGroups, searchValue),
    [formattePlaylistGroups, searchValue],
  );

  const sortedGroups = useMemo(
    () => sortPlaylists(filteredGroups, sortValue),
    [filteredGroups, sortValue],
  );

  const handleSearch = (e: ChangeEvent<HTMLInputElement>): void => {
    setSearchValue(e.currentTarget.value);
  };

  return (
    <Box className={`${bg100} p-4 h-[100%]`}>
      <Flex className={`flex-col gap-4`}>
        <Box className={`flex w-[100%]`}>
          <Button
            data-testid="new-playlist-button"
            onClick={newPlaylistModal.show}
            variant="outline"
            startIcon={<Icon icon={faPlus} />}
            className={`w-[100%] ${textBlue} border-blue-200`}
          >
            <FormattedMessage
              id="playlist.sideNav.newPlaylistButton"
              defaultMessage="New playlist"
            />
          </Button>
        </Box>
        {countPlaylists(formattePlaylistGroups) > 14 && (
          <>
            <Divider />
            <PlaylistSortByButton setSortValue={setSortValue} />
            <PlaylistSearchField
              searchValue={searchValue}
              handleSearch={handleSearch}
            />
          </>
        )}
      </Flex>

      <Box className={`p-8`}>
        {sortedGroups.map((group) => (
          <Box key={group.type} className={`mb-4`}>
            <PlaylistGroupAccordion playlistGroup={group} />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default PlaylistsSideNav;
